import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiCamera, FiLogOut, FiMoreHorizontal, FiPlus } from 'react-icons/fi';

/**
 * Bottom tab bar shown on phones (hidden on md+). Used by BOTH experiences:
 *  - Staff/mechanic: the few menu items + center QR-scan FAB + Logout.
 *  - Admin/Manager/etc.: curated primary destinations + center "+" quick-create
 *    FAB + a "More" button that opens the full sidebar drawer.
 *
 * The center FAB is kept VISUALLY CENTERED by splitting the menu items into a
 * left and right half around it. `onCreate` → center is a "+" (quick create);
 * otherwise it's the QR-scan shortcut. `onMore` → trailing "More"; otherwise
 * trailing Logout.
 */
const MobileBottomNav = ({ menuItems, onScan, onLogout, onMore, onCreate }) => {
  const location = useLocation();
  const { t } = useTranslation();

  const isActive = (path) =>
    location.pathname === path || (path !== '/' && location.pathname.startsWith(`${path}/`));

  const renderTab = (item) => {
    const Icon = item.icon;
    const active = isActive(item.path);
    return (
      <Link
        key={item.path}
        to={item.path}
        aria-current={active ? 'page' : undefined}
        className={`relative flex flex-col items-center justify-center gap-1 py-2 flex-1 min-w-0 text-[11px] font-medium transition-colors ${
          active ? 'text-accent' : 'text-content-muted hover:text-content'
        }`}
      >
        {active && (
          <span className="absolute top-0 h-0.5 w-8 rounded-full bg-accent" aria-hidden="true" />
        )}
        <Icon size={22} />
        <span className="max-w-full truncate px-0.5">{t(item.i18nKey)}</span>
      </Link>
    );
  };

  // Split items around the centered FAB so it sits in the visual middle.
  const half = Math.ceil(menuItems.length / 2);
  const leftItems = menuItems.slice(0, half);
  const rightItems = menuItems.slice(half);

  return (
    <nav className="shrink-0 z-40 md:hidden bg-surface/95 backdrop-blur border-t border-edge pb-[env(safe-area-inset-bottom)]">
      <div className="flex items-stretch justify-around">
        {leftItems.map(renderTab)}

        {/* Center FAB: "+" quick-create when onCreate is given, else QR scan. */}
        <div className="flex-1 flex items-center justify-center">
          <button
            onClick={onCreate || onScan}
            className="-mt-6 w-14 h-14 rounded-full bg-gradient-to-br from-accent to-accent-alt text-white dark:text-slate-900 shadow-lg flex items-center justify-center active:scale-95 transition-transform"
            aria-label={onCreate ? t('qc.title') : t('scanQR')}
          >
            {onCreate ? <FiPlus size={26} /> : <FiCamera size={24} />}
          </button>
        </div>

        {rightItems.map(renderTab)}

        {onMore ? (
          <button
            onClick={onMore}
            className="flex flex-col items-center justify-center gap-1 py-2 flex-1 min-w-0 text-[11px] font-medium text-content-muted hover:text-content transition-colors"
          >
            <FiMoreHorizontal size={22} />
            <span className="max-w-full truncate px-0.5">{t('cm.header.more')}</span>
          </button>
        ) : (
          <button
            onClick={onLogout}
            className="flex flex-col items-center justify-center gap-1 py-2 flex-1 min-w-0 text-[11px] font-medium text-content-muted hover:text-red-500 transition-colors"
          >
            <FiLogOut size={22} className="rtl:-scale-x-100" />
            <span className="max-w-full truncate px-0.5">{t('logout')}</span>
          </button>
        )}
      </div>
    </nav>
  );
};

export default MobileBottomNav;
